import { gql } from '@apollo/client';

/**
 * AB 整站作废「退役预览」（2.1 需求点2）。
 *
 * **两个调用时机，对齐 PRD 的两个检查点：**
 *
 * 1. **点击AB映射操作时**（进入向导前，见 `ReviewAddressDetail.handleOperation`）：
 *    传 `targetBCooperationWarehouseId: null` + `fullySelected: true`，只看
 *    `HAS_IN_PROGRESS_APPOINTMENT` —— 命中就弹窗列出在途单、**不打开向导**
 *    （PRD：「是，弹窗提示…请操作取消预约单或延后处理 / 否，进入操作」）。
 *    这一刻其余 reason 一律放行：它们讲的是「会不会退役」，属 PRD 的「操作后」分支。
 * 2. **提交前**（见 `AbMappingWizard.doAbSubmit`）：传真实 B 与勾选状态，拿完整退役结论；
 *    同时兜住入口到提交之间的时间窗（期间可能有单被派出去）。
 *
 * ⚠️ 提交前这一次，「被挡」不等于「不能提交」。被挡的语义是「AB 映射照做，只是不整站作废」，
 * 而运营的意图往往就是只做映射。所以要按 blockedReason 分三层处理，
 * 别一律拦死（分层规则见 AbMappingWizard.handleConfirm 的注释）。
 */
export const PREVIEW_AB_RETIRE = gql`
    query PreviewAbRetire($reviewPoolId: Long!, $targetBCooperationWarehouseId: Long, $fullySelected: Boolean!) {
        previewAbRetire(
            reviewPoolId: $reviewPoolId
            targetBCooperationWarehouseId: $targetBCooperationWarehouseId
            fullySelected: $fullySelected
        ) {
            applicable
            willRetire
            blockedReason
            cooperationWarehouseId
            cooperationWarehouseName
            addressCount
            cityCount
            contactCount
            channelCount
            estimatedRows
            maxCityCount
            maxAddressCount
            maxContactCount
            maxChannelCount
            maxEstimatedRows
            blockingAppointments {
                appointmentNumber
                expectedPickupDate
                status
            }
        }
    }
`;

/** 后端 AbRetirePreviewDTO.REASON_* 常量，与 pickup-api 一一对应。 */
export enum AbRetireBlockedReason {
    /** 功能未开启 —— 当前默认就是关的，属正常场景 */
    Disabled = 'DISABLED',
    /** 条目不适用（非历史存量 / 无关联站点）—— 系统进单条目本就不退役，属正常场景 */
    NotApplicable = 'NOT_APPLICABLE',
    /** 只勾了部分维度 —— 合法的分批操作，条目会保留待办 */
    NotFullySelected = 'NOT_FULLY_SELECTED',
    /** 目标 B 就是本条目关联的站点自身 */
    SelfTarget = 'SELF_TARGET',
    /** 本站点是他人 AB 映射的目标端 */
    IsAbTarget = 'IS_AB_TARGET',
    /** 联系人 JSON 读不出手机号（是"读不出"而非"没有"，不能当空集删站） */
    ContactUnparseable = 'CONTACT_UNPARSEABLE',
    /** 长期揽收站点 —— 作废会掐断循环开单 */
    StandingPickupStation = 'STANDING_PICKUP_STATION',
    /** 关联地址跨多个城市 */
    MultiCity = 'MULTI_CITY',
    /** 关联地址数超限 */
    TooManyAddresses = 'TOO_MANY_ADDRESSES',
    /** 可用联系人数超限 */
    TooManyContacts = 'TOO_MANY_CONTACTS',
    /** 关联渠道数超限 */
    TooManyChannels = 'TOO_MANY_CHANNELS',
    /** 预计写入行数超限 */
    TooManyRows = 'TOO_MANY_ROWS',
    /** 站点存在进行中预约单 —— 唯一需要**阻断提交**的原因 */
    HasInProgressAppointment = 'HAS_IN_PROGRESS_APPOINTMENT',
}

export interface AppointmentBrief {
    appointmentNumber?: string | null;
    expectedPickupDate?: string | null;
    status?: string | null;
}

export interface AbRetirePreview {
    applicable: boolean;
    willRetire: boolean;
    blockedReason?: string | null;
    cooperationWarehouseId?: number | null;
    cooperationWarehouseName?: string | null;
    addressCount: number;
    cityCount: number;
    contactCount: number;
    channelCount: number;
    estimatedRows: number;
    maxCityCount: number;
    maxAddressCount: number;
    maxContactCount: number;
    maxChannelCount: number;
    maxEstimatedRows: number;
    blockingAppointments: AppointmentBrief[];
}

export interface PreviewAbRetireData {
    previewAbRetire: AbRetirePreview;
}

/**
 * 属于「正常场景」的被挡原因：不提示、不阻断，直接提交。
 *
 * 这三种都不是异常：功能没开是当前默认态；系统进单条目本来就不走整站作废；
 * 只勾部分维度是产品设计的分批操作路径。对它们弹窗只会训练运营无脑点确定。
 */
export const SILENT_BLOCKED_REASONS: string[] = [
    AbRetireBlockedReason.Disabled,
    AbRetireBlockedReason.NotApplicable,
    AbRetireBlockedReason.NotFullySelected,
];
